import React, { useMemo, useState } from 'react';
import { X, Upload, Loader2, Search, Link2, Link2Off, FolderOpen, Eye, Download, Stamp } from 'lucide-react';
import { Button } from '../Button';
import { UserProfile } from '../../types';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { holdIdleExit } from '../../services/idleHold';
import { useRechte } from '../../hooks/useRechte';
import { setzeAdminSperre, updateDokument, uploadDokument } from '../../services/projektDokumenteService';
import { useDokumente } from './useDokumente';
import { DokumenteListe } from './DokumenteListe';
import { DokumentUploadDialog } from './DokumentUploadDialog';
import { dokumentAnsehen, dokumentHerunterladen } from './dokumentDatei';
import {
  DOKUMENTE_JE_SEITE, ERZEUGT_HINWEIS, ERZEUGT_KENNZEICHEN, darfAdminSchloss, erzeugtVon, filterDokumente,
  formatBytes, kategorieLabel, modulLabel, ordnerVorschlaege as ordnerAus, sortiereDokumente, toggleModul,
} from './dokumenteLogic';
import { DokumentEntwurf, DokumentModul, ProjektDokument } from './types';

interface Props {
  projectId: string;
  /** Aus WELCHEM Modul heraus die Ablage geoeffnet wurde. */
  modul: DokumentModul;
  userProfile?: UserProfile | null;
  isReadOnly?: boolean;
  onSchliessen: () => void;
}

/**
 * Die Projektablage, eingebettet in ein anderes Modul (SiGe-Plan, Unterlage
 * fuer spaetere Arbeiten, Handbuch, Planungsphase, Koordinierung,
 * Baustellenordnung, Vorankuendigung, Einweisung — Plan 1A.4: ueberall
 * dieselbe Ablage).
 *
 * Das ist der Kern der Anforderung: die Dokumente gehoeren nicht dem
 * Koordinator allein. Wer im SiGe-Plan arbeitet, kommt hier an die Plaene und
 * Beurteilungen, ohne das Modul zu verlassen — und kann direkt aus dem Plan
 * heraus ablegen. Die vom Modul selbst abgelegten Fassungen
 * (`services/unterlagenAblage.ts`, `module: [modul]`) erscheinen hier mit
 * Kennzeichen; loeschen laesst sich aus dem Panel nichts.
 *
 * Zwei Ansichten:
 *   „Diesem Modul zugeordnet"     — was hier hingehoert (`module` enthaelt das Modul)
 *   „Alle Dokumente des Projekts" — alles, mit Ansehen/Herunterladen und einem
 *                                   Schalter je Zeile zum Zuordnen
 *
 * Es gibt bewusst KEINE eigene Kopie und keinen eigenen Bucket je Modul: ein
 * Dokument liegt genau einmal im Projekt und traegt nur die Kennzeichnung, wo
 * es zusaetzlich auftauchen soll. Alles andere fuehrte dazu, dass eine
 * korrigierte Fassung an einer Stelle aktualisiert wird und an drei anderen
 * nicht.
 */
export const DokumentePanel: React.FC<Props> = ({
  projectId, modul, userProfile, isReadOnly = false, onSchliessen,
}) => {
  // 'temp-id' ist die Vorbelegung eines noch nie gespeicherten Projekts
  // (App.tsx, ProjectHub.tsx). Ein Projekt ohne Zeile in der Datenbank kann
  // keine Ablage haben — und ein Upload darauf liefe in eine Storage-Policy,
  // die kein passendes Projekt findet. Jeder andere Verbraucher der
  // Projekt-Id im Repo prueft das ebenso (useProjectHubData, ProjectArchiveView).
  const gespeichert = Boolean(projectId) && projectId !== 'temp-id';
  const { dokumente, laden, fehler, uebernehmen } = useDokumente(gespeichert ? projectId : null);
  const [ansicht, setAnsicht] = useState<'modul' | 'alle'>('modul');
  const [suche, setSuche] = useState('');
  const [uploadOffen, setUploadOffen] = useState(false);
  const [zuordnungLaeuft, setZuordnungLaeuft] = useState<string | null>(null);
  /** Zeile, deren Datei gerade geoeffnet oder geladen wird (Ansicht „Alle"). */
  const [dateiLaeuft, setDateiLaeuft] = useState<string | null>(null);
  const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
  /** Ordner, die die Liste nach einem Upload oeffnen soll — siehe DokumenteListe. */
  const [aufklappen, setAufklappen] = useState<string[]>([]);
  /** Zeilen der Zuordnungsansicht; dieselbe Grenze wie je Ordner in der Liste. */
  const [alleGrenze, setAlleGrenze] = useState(DOKUMENTE_JE_SEITE);

  const heute = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const vorschlaege = useMemo(() => ordnerAus(dokumente), [dokumente]);

  const zugeordnet = useMemo(
    () => dokumente.filter(d => d.module.includes(modul)),
    [dokumente, modul],
  );

  const sichtbar = useMemo(() => {
    const grundmenge = ansicht === 'modul' ? zugeordnet : dokumente;
    return sortiereDokumente(filterDokumente(grundmenge, { suche }, heute), 'neueste');
  }, [ansicht, zugeordnet, dokumente, suche, heute]);

  const readOnlyGrund = readOnlyTitle('Dokumente lassen sich nicht ablegen oder zuordnen.');

  /** Anzeige-Entscheidung, deckungsgleich mit der Datenbank — siehe DokumenteModul. */
  const rechte = useRechte(userProfile?.role);
  const darfSchloss = darfAdminSchloss(rechte);

  /** Zweite Reihe gegen den Lesemodus — Begruendung wie in DokumenteModul. */
  const abgelehntWennNurAnsicht = (was: string): boolean => {
    if (!isReadOnly) return false;
    alert(readOnlyMessage(was));
    return true;
  };

  const zuordnungUmschalten = async (d: ProjektDokument) => {
    if (abgelehntWennNurAnsicht('Die Zuordnung wurde nicht geändert.')) return;
    setZuordnungLaeuft(d.id);
    setAktionsfehler(null);
    try {
      const geaendert = await updateDokument(d.id, { module: toggleModul(d.module, modul) });
      uebernehmen(geaendert);
    } catch (e) {
      setAktionsfehler(rechtFehlerOder(e));
    } finally {
      setZuordnungLaeuft(null);
    }
  };

  const sperreUmschalten = async (d: ProjektDokument) => {
    if (abgelehntWennNurAnsicht('Das Admin-Schloss wurde nicht geändert.')) return;
    uebernehmen(await setzeAdminSperre(d.id, !d.nur_admin));
  };

  /**
   * Ansehen/Herunterladen in der Ansicht „Alle Dokumente des Projekts". In
   * der Modulansicht uebernimmt das die Liste selbst; hier hat die knappe
   * Zuordnungszeile keinen eigenen Fehlerkasten und meldet ueber
   * `aktionsfehler`.
   */
  const dateiAktion = async (d: ProjektDokument, was: (d: ProjektDokument) => Promise<void>) => {
    setDateiLaeuft(d.id);
    setAktionsfehler(null);
    try {
      await was(d);
    } catch (e) {
      setAktionsfehler(rechtFehlerOder(e));
    } finally {
      setDateiLaeuft(null);
    }
  };

  const hochladen = async (datei: File, entwurf: DokumentEntwurf) => {
    if (abgelehntWennNurAnsicht('Die Datei wurde nicht abgelegt.')) return;
    const freigeben = holdIdleExit('Dokumenten-Upload läuft');
    try {
      const angelegt = await uploadDokument({
        projectId, datei, dateiname: datei.name, entwurf,
        benutzerId: userProfile?.id,
        benutzerName: [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || userProfile?.email,
      });
      uebernehmen(angelegt);
      setAufklappen([angelegt.ordner]);
    } finally {
      freigeben();
    }
  };

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-800">Dokumente</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              Projektablage — Ansicht für „{modulLabel(modul)}"
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary" size="sm" onClick={() => setUploadOffen(true)}
              disabled={isReadOnly || !gespeichert}
              title={isReadOnly ? readOnlyGrund : !gespeichert ? 'Das Projekt muss erst gespeichert werden.' : undefined}
              icon={<Upload className="w-4 h-4" />}
            >
              Ablegen
            </Button>
            <button onClick={onSchliessen} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 shrink-0 bg-slate-50">
          <button
            onClick={() => setAnsicht('modul')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
              ansicht === 'modul' ? 'bg-blue-600 text-white shadow' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
            }`}
          >
            Diesem Modul zugeordnet ({zugeordnet.length})
          </button>
          <button
            onClick={() => setAnsicht('alle')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${
              ansicht === 'alle' ? 'bg-blue-600 text-white shadow' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
            }`}
          >
            Alle Dokumente des Projekts ({dokumente.length})
          </button>
          <div className="relative flex-1 min-w-[180px] group">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              value={suche} onChange={e => setSuche(e.target.value)} placeholder="Suchen…"
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          {/* Beide Meldungen getrennt: ein Zuordnungsfehler darf nicht
              unsichtbar bleiben, nur weil noch ein Ladefehler steht. */}
          {fehler && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 whitespace-pre-line">{fehler}</div>
          )}
          {aktionsfehler && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 whitespace-pre-line">{aktionsfehler}</div>
          )}

          {!gespeichert ? (
            <div className="py-12 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
              <FolderOpen className="w-9 h-9 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-semibold text-slate-500">Das Projekt ist noch nicht gespeichert.</p>
              <p className="text-xs mt-1">Sobald es einmal gespeichert wurde, steht die Dokumentenablage bereit.</p>
            </div>
          ) : laden ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
          ) : ansicht === 'modul' ? (
            sichtbar.length === 0 ? (
              <div className="py-12 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                <FolderOpen className="w-9 h-9 mx-auto mb-3 opacity-20" />
                {zugeordnet.length === 0 ? (
                  <>
                    <p className="text-sm font-semibold text-slate-500">Diesem Modul ist noch kein Dokument zugeordnet.</p>
                    <p className="text-xs mt-1">
                      Über „Alle Dokumente des Projekts" lässt sich Vorhandenes zuordnen — oder legen Sie hier direkt etwas ab.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-500">Zu dieser Suche passt hier nichts.</p>
                    <p className="text-xs mt-1">{zugeordnet.length} zugeordnete {zugeordnet.length === 1 ? 'Datei ist' : 'Dateien sind'} vorhanden.</p>
                  </>
                )}
              </div>
            ) : (
              <DokumenteListe
                dokumente={sichtbar} heute={heute} zeigeKategorie
                isReadOnly={isReadOnly} readOnlyHinweis={readOnlyGrund}
                onAnsehen={dokumentAnsehen} onHerunterladen={dokumentHerunterladen}
                darfSchloss={darfSchloss} onSperreUmschalten={sperreUmschalten}
                filterSchluessel={suche.trim()} aufklappen={aufklappen}
              />
            )
          ) : (
            /* Alle Dokumente des Projekts: bewusst eine eigene, knappe Zeile
               und nicht die Dateiliste — hier geht es um die Frage je Dokument
               (gehört das hierher?) und ums Nachsehen (Ansehen, Herunterladen),
               nicht um Bearbeiten oder Löschen. */
            <div className="space-y-2">
              {sichtbar.slice(0, alleGrenze).map(d => {
                const an = d.module.includes(modul);
                const laeuft = zuordnungLaeuft === d.id;
                const dateiBeschaeftigt = dateiLaeuft === d.id;
                const erzeugt = erzeugtVon(d);
                return (
                  <div key={d.id} className="bg-white p-3 rounded-xl border border-slate-200 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">{d.titel}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {kategorieLabel(d.kategorie)}{d.ordner ? ` · ${d.ordner}` : ''} · {formatBytes(d.groesse_bytes)}
                        {/* Auch hier kenntlich: wer ein gesperrtes Dokument
                            einem Modul zuordnet, aendert an der Sperre nichts —
                            es bleibt fuer Projektbeteiligte unsichtbar, und das
                            soll beim Zuordnen niemanden ueberraschen. */}
                        {d.nur_admin && <span className="text-indigo-600 font-bold"> · nur Verwaltung</span>}
                      </p>
                      {erzeugt && (
                        <span
                          title={ERZEUGT_HINWEIS}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                        >
                          <Stamp className="w-3 h-3" /> {ERZEUGT_KENNZEICHEN} · {modulLabel(erzeugt)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => void dateiAktion(d, dokumentAnsehen)}
                      disabled={dateiBeschaeftigt}
                      title="Ansehen"
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
                    >
                      {dateiBeschaeftigt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => void dateiAktion(d, dokumentHerunterladen)}
                      disabled={dateiBeschaeftigt}
                      title="Herunterladen"
                      className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => void zuordnungUmschalten(d)}
                      disabled={isReadOnly || laeuft}
                      title={isReadOnly ? readOnlyGrund : an ? 'Zuordnung aufheben' : `„${modulLabel(modul)}" zuordnen`}
                      className={`min-h-[44px] px-3 rounded-lg text-xs font-semibold border transition-colors flex items-center gap-1.5 disabled:opacity-40 ${
                        an ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : an ? <Link2 className="w-3.5 h-3.5" /> : <Link2Off className="w-3.5 h-3.5" />}
                      {an ? 'zugeordnet' : 'zuordnen'}
                    </button>
                  </div>
                );
              })}
              {sichtbar.length > alleGrenze && (
                <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
                  <span className="text-[11px] text-slate-400">{alleGrenze} von {sichtbar.length} angezeigt</span>
                  <button
                    type="button" onClick={() => setAlleGrenze(g => g + DOKUMENTE_JE_SEITE)}
                    className="min-h-[36px] px-3 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    Weitere {Math.min(sichtbar.length - alleGrenze, DOKUMENTE_JE_SEITE)} anzeigen
                  </button>
                  {sichtbar.length - alleGrenze > DOKUMENTE_JE_SEITE && (
                    <button
                      type="button" onClick={() => setAlleGrenze(sichtbar.length)}
                      className="min-h-[36px] px-3 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                    >
                      Alle {sichtbar.length} anzeigen
                    </button>
                  )}
                </div>
              )}
              {!sichtbar.length && (
                <div className="py-12 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                  <p className="text-sm font-semibold text-slate-500">In der Projektablage liegt (noch) nichts Passendes.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {uploadOffen && gespeichert && (
        <DokumentUploadDialog
          ordnerVorschlaege={vorschlaege}
          isReadOnly={isReadOnly}
          darfSchloss={darfSchloss}
          modulVorgabe={[modul]}
          onAbbrechen={() => setUploadOffen(false)}
          onHochladen={hochladen}
        />
      )}
    </div>
  );
};
